import React, { useCallback, useState, useMemo, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import { TimeSlot } from "../../../../types/booking";
import { useBookingData } from "../../../../providers/BookingDataProvider";
import { useTheme } from "../../../../theme/ThemeProvider";
import CalendarViewModel from "../../../../viewModels/calendarViewModel";
import BookingViewModel from "../../../../viewModels/bookingViewModel";
import { BookingFormData } from "../../../../types/booking";
import Button from "../../../../components/core/Button";
import { format } from "date-fns";
import { formatDateInTimezone } from "../../../../utils/timezones";
import { useBookingPage } from "../../BookingPageContext";
import DynamicFormField from "../../../../components/DynamicFormField/DynamicFormField";

interface BookingFormStepProps {
  bookingForm: BookingFormData;
  onInputChange: (field: string, value: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  onBack: () => void;
  isSubmitting: boolean;
  error: string | null;
  selectedDate: Date;
  selectedTime: TimeSlot | null;
  selectedTimezone: string;
  mode?: "booking" | "reschedule" | "preview";
  /** Event waitlist: same form, but submit joins the waitlist instead of booking */
  waitlistMode?: boolean;
}

/**
 * BookingFormStep Component
 *
 * Displays the booking form and handles form submission.
 * In reschedule mode, fields are locked (read-only) by default with an "Edit" button
 * to unlock them, since the guest's info is pre-populated from the original booking.
 */
const BookingFormStep: React.FC<BookingFormStepProps> = ({
  bookingForm,
  onInputChange,
  onSubmit,
  onBack,
  isSubmitting,
  error,
  selectedDate,
  selectedTime,
  selectedTimezone,
  mode = "booking",
  waitlistMode = false,
}) => {
  const theme = useTheme();
  const { meetingType } = useBookingData();
  const { t } = useTranslation();

  // Get form template from context
  const { formTemplate, loadingFormTemplate } = useBookingPage();

  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});

  // In reschedule mode, fields start locked (read-only) with pre-populated data
  const isRescheduleMode = mode === "reschedule";
  const [isFormLocked, setIsFormLocked] = useState(isRescheduleMode);

  // Get all enabled fields from template, sorted by order
  const allTemplateFields = useMemo(() => {
    if (!formTemplate || !formTemplate.fields) {
      return [];
    }

    return (formTemplate.fields || [])
      .filter(field => field.enabled !== false)
      .sort((a, b) => a.order - b.order);
  }, [formTemplate]);

  // Format the selected date and time for display with proper timezone handling
  const formattedDate = useMemo(() => {
    if (!selectedDate) return "";

    // Use the formatDateInTimezone utility to ensure proper timezone handling
    return formatDateInTimezone(selectedDate, selectedTimezone, {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  }, [selectedDate, selectedTimezone]);

  const formattedTime = useMemo(() => {
    if (!selectedTime) return "";

    // Use the formatTimeSlot method from CalendarViewModel with explicit timezone
    return CalendarViewModel.formatTimeSlot(selectedTime, selectedTimezone);
  }, [selectedTime, selectedTimezone]);

  // Handle form input changes
  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { name, value } = e.target;
      onInputChange(name, value);

      // Clear validation error when user types
      if (validationErrors[name]) {
        setValidationErrors((prev) => {
          const updated = { ...prev };
          delete updated[name];
          return updated;
        });
      }
    },
    [onInputChange, validationErrors]
  );

  // Handle form submission with validation
  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();

      // Validate all template fields
      const errors: Record<string, string> = {};
      allTemplateFields.forEach(field => {
        const value = (bookingForm as any)[field.id];

        // Required field validation
        if (field.required) {
          if (!value || (typeof value === 'string' && value.trim() === '') || (Array.isArray(value) && value.length === 0)) {
            errors[field.id] = t('forms:validation.required', {
              field: field.label,
              defaultValue: `${field.label} is required`
            });
          }
        }

        // Only validate if value exists (skip validation for empty optional fields)
        if (value && typeof value === 'string' && value.trim() !== '') {
          // Email validation for email fields
          if (field.type === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
            errors[field.id] = t('forms:validation.emailInvalid', {
              field: field.label,
              defaultValue: `Valid ${field.label} is required`
            });
          }

          // Number validation
          if (field.type === 'number') {
            const numValue = parseFloat(value);
            if (isNaN(numValue)) {
              errors[field.id] = t('forms:validation.mustBeNumber', {
                field: field.label,
                defaultValue: `${field.label} must be a number`
              });
            } else if (field.validation?.min !== undefined && numValue < field.validation.min) {
              errors[field.id] = t('forms:validation.minValue', {
                field: field.label,
                min: field.validation.min,
                defaultValue: `${field.label} must be at least ${field.validation.min}`
              });
            } else if (field.validation?.max !== undefined && numValue > field.validation.max) {
              errors[field.id] = t('forms:validation.maxValue', {
                field: field.label,
                max: field.validation.max,
                defaultValue: `${field.label} must be at most ${field.validation.max}`
              });
            }
          }

          // Length validation
          if (field.validation?.minLength && value.length < field.validation.minLength) {
            errors[field.id] = t('forms:validation.minLength', {
              field: field.label,
              min: field.validation.minLength,
              defaultValue: `${field.label} must be at least ${field.validation.minLength} characters`
            });
          }
          if (field.validation?.maxLength && value.length > field.validation.maxLength) {
            errors[field.id] = t('forms:validation.maxLength', {
              field: field.label,
              max: field.validation.maxLength,
              defaultValue: `${field.label} must be at most ${field.validation.maxLength} characters`
            });
          }
        }
      });

      if (Object.keys(errors).length > 0) {
        setValidationErrors(errors);
        return;
      }

      // Clear validation errors and submit form directly
      setValidationErrors({});
      onSubmit(e);
    },
    [bookingForm, onSubmit, allTemplateFields]
  );

  return (
    <div className="bg-white rounded-lg p-4 md:p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold text-gray-800">
          {isRescheduleMode
            ? t('booking:steps.confirmReschedule', 'Confirm reschedule')
            : t('booking:steps.enterDetails', 'Complete booking')}
        </h2>
        <Button
          variant="secondary"
          size="sm"
          onClick={onBack}
          startIcon={
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M9.707 14.707a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 1.414L7.414 9H15a1 1 0 110 2H7.414l2.293 2.293a1 1 0 010 1.414z"
                clipRule="evenodd"
              />
            </svg>
          }
        >
          {t('booking:navigation.back', 'Back')}
        </Button>
      </div>

      {/* Error message */}
      {error && (
        <div className="mb-4 bg-red-50 text-red-700 p-3 rounded-md animate-shake">
          {error}
        </div>
      )}

      {/* Waitlist note — this slot is full; the same form joins the waitlist */}
      {waitlistMode && (
        <div className="mb-4 p-3 rounded-lg bg-gray-50 border border-gray-200 text-sm text-gray-600">
          {t(
            "booking:waitlist.joinDescription",
            "This time slot is full. Join the waitlist and we'll email you a personal booking link if a spot opens up — first come, first served."
          )}
        </div>
      )}

      {/* Booking form */}
      <form onSubmit={handleSubmit}>
        {/* Grid container for responsive field layout */}
        <div className="grid grid-cols-12 gap-4 mb-6">
          {/* Render all fields from form template */}
          {allTemplateFields.map((field) => (
            <DynamicFormField
              key={field.id}
              field={field}
              value={(bookingForm as any)[field.id] || ''}
              onChange={onInputChange}
              error={validationErrors[field.id]}
              disabled={isRescheduleMode && isFormLocked}
            />
          ))}
        </div>

        {/* Action buttons */}
        <div className="flex items-center justify-end gap-4 mt-6">
          {/* Edit button - only in reschedule mode while fields are locked */}
          {isRescheduleMode && isFormLocked && (
            <button
              type="button"
              onClick={() => setIsFormLocked(false)}
              className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
            >
              {t('booking:reschedule.editDetails', 'Edit')}
            </button>
          )}
          <Button
            type="submit"
            isLoading={isSubmitting}
            disabled={isSubmitting}
            className="w-full sm:w-auto"
          >
            {isRescheduleMode
              ? t('booking:form.confirmReschedule', 'Confirm Reschedule')
              : waitlistMode
                ? t('booking:timeSlots.capacity.joinWaitlist', 'Join Waitlist')
                : t('booking:form.confirmBooking', 'Confirm Booking')}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default BookingFormStep;
